// Form validation utilities
import type { ValidationRule, ValidationResult } from './validation.types';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Password validation regex (at least 6 characters, 1 letter, 1 number)
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/;

export const validateField = (
  value: string,
  rules: ValidationRule
): string | null => {
  // Required check
  if (rules.required && (!value || value.trim() === "")) {
    return "Ce champ est requis";
  }

  // Skip other validations if value is empty and not required
  if (!value || value.trim() === "") {
    return null;
  }

  // Min length check
  if (rules.minLength && value.length < rules.minLength) {
    return `Longueur minimale requise : ${rules.minLength} caractères`;
  }

  // Max length check
  if (rules.maxLength && value.length > rules.maxLength) {
    return `Longueur maximale requise : ${rules.maxLength} caractères`;
  }

  // Pattern check
  if (rules.pattern && !rules.pattern.test(value)) {
    return "Format invalide";
  }

  // Custom validation
  if (rules.custom) {
    return rules.custom(value);
  }

  return null;
};

export const validateForm = (
  data: Record<string, string>,
  rules: Record<string, ValidationRule>
): ValidationResult => {
  const errors: Record<string, string> = {};

  Object.keys(rules).forEach((field) => {
    const value = data[field] || "";
    const fieldRules = rules[field];
    const error = validateField(value, fieldRules);

    if (error) {
      errors[field] = error;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Common validation rules
export const validationRules = {
  email: {
    required: true,
    pattern: EMAIL_REGEX,
    custom: (value: string) => {
      if (!EMAIL_REGEX.test(value)) {
        return "Please enter a valid email address";
      }
      return null;
    },
  },

  password: {
    required: true,
    minLength: 6,
    custom: (value: string) => {
      if (value.length < 6) {
        return "Password must be at least 6 characters long";
      }
      if (!PASSWORD_REGEX.test(value)) {
        return "Password must contain at least one letter and one number";
      }
      return null;
    },
  },

  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    custom: (value: string) => {
      if (value.trim().length < 2) {
        return "Name must be at least 2 characters long";
      }
      if (!/^[a-zA-Z\s]+$/.test(value)) {
        return "Name can only contain letters and spaces";
      }
      return null;
    },
  },

  phone: {
    required: false,
    pattern: /^[+]?[1-9][\d]{0,15}$/,
    custom: (value: string) => {
      if (value && !/^[+]?[1-9][\d]{0,15}$/.test(value)) {
        return "Please enter a valid phone number";
      }
      return null;
    },
  },
};

// Sanitize input to prevent XSS
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, "") // Remove potential HTML tags
    .replace(/javascript:/gi, "") // Remove javascript: protocol
    .replace(/on\w+=/gi, ""); // Remove event handlers
};

// Format error messages
export const formatErrorMessage = (error: string): string => {
  return error.charAt(0).toUpperCase() + error.slice(1);
};
